import React, { useEffect, useState } from "react";
import { BackToPlatform } from "@Light/components/auth/BackToPlatform";
import { CloseWindowPrompt } from "@Light/components/auth/CloseWindowPrompt";
import { useInfo } from "./WithInfo";
import { Overlay } from "@Light/components/page/Overlay";
import { WithHeader } from "@Light/components/page/header/Header";

const CLOSE_WINDOW_PROMPT_DELAY_MS = 500;

export type FlowEmbedProps = {
  src: string;
  title?: string;
  className?: string;
};

export const FlowEmbed: React.FC<FlowEmbedProps> = ({
  src,
  title = "Flow Portal",
  className = "",
}) => {
  const [showBackToPlatform, setShowBackToPlatform] = useState(false);
  const [showClosePrompt, setShowClosePrompt] = useState(false);
  const [pendingWindowClose, setPendingWindowClose] = useState(false);
  const info = useInfo();
  const appUrl = info?.configuration?.app_url;

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const eventType = event.data?.type;
      if (!eventType) {
        return;
      }
      if (eventType === "light-flow-close") {
        if (appUrl) {
          setShowBackToPlatform(true);
        } else {
          setPendingWindowClose(true);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [appUrl]);

  useEffect(() => {
    if (!pendingWindowClose) {
      return;
    }

    window.close();

    const timeoutId = window.setTimeout(() => {
      setShowClosePrompt(true);
    }, CLOSE_WINDOW_PROMPT_DELAY_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [pendingWindowClose]);

  if (showBackToPlatform && appUrl) {
    return (
      <Overlay>
        <WithHeader>
          <BackToPlatform appUrl={appUrl} />
        </WithHeader>
      </Overlay>
    );
  }

  if (showClosePrompt) {
    return (
      <Overlay>
        <WithHeader>
          <CloseWindowPrompt />
        </WithHeader>
      </Overlay>
    );
  }

  return (
    <div
      className={`fixed inset-0 w-screen overflow-hidden ${className}`}
      style={{ height: "100dvh" }}
    >
      <iframe
        src={src}
        className="w-full h-full border-0 block"
        title={title}
        allowFullScreen
      />
    </div>
  );
};
